import { z } from 'zod'
import { acceptProposalInputSchema } from '@lib/input/proposal-selection.input'
import { proposalPlanSchema } from '@lib/enums/proposal-plan.enum'
import type { PlanState, SharedVersion } from '@/proposal-document/choosing'

/**
 * A reader's plan and changes, carried in a link so one board member can send
 * their version to another.
 *
 * Web-only: nothing here reaches the API. The link opens the same proposal
 * with these choices already made, and the recipient can still change them or
 * accept. It lives in the URL fragment, which browsers never send to a server,
 * so a forwarded link does not put somebody's choices in an access log.
 *
 * Validated on the way back in, like any other untrusted input: a link that
 * was edited by hand or cut short opens the proposal as offered rather than
 * failing.
 */
const adjustmentsSchema = acceptProposalInputSchema
  .omit({ plan: true, budgetAmount: true })
  .extend({
    includedTrees: z
      .array(
        z
          .object({
            site: z.string().trim().min(1).max(200),
            label: z.string().trim().min(1).max(12),
          })
          .strict(),
      )
      .max(2000)
      .default([]),
    // Repairs a plan left out and the client added back. Defaulted, so a
    // link shared before irrigation could be bid still opens.
    includedRepairs: z
      .array(
        z
          .object({
            site: z.string().trim().min(1).max(200),
            key: z.string().trim().min(1).max(64),
          })
          .strict(),
      )
      .max(2000)
      .default([]),
  })

const versionLinkSchema = z
  .object({
    // Any plan ever offered: a link can outlive a plan, and the page opens it
    // on the recommendation instead (see `useChoosing`).
    plan: proposalPlanSchema,
    // Only links from when "Fit to a budget" was offered carry this. It is
    // read so they still open, and then forgotten.
    budgetCents: z
      .number()
      .int()
      .nonnegative()
      .max(10_000_000_000)
      .nullable()
      .optional(),
    adjustments: adjustmentsSchema,
  })
  .strict()

/** The fragment key a version travels under: `#version=...`. */
const KEY = 'version'

function toBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64Url(text: string): string | null {
  try {
    const padded = text.replace(/-/g, '+').replace(/_/g, '/')
    const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0))
    return new TextDecoder().decode(bytes)
  } catch {
    return null
  }
}

/** The fragment that carries a version, without the leading `#`. */
export function encodeVersion(state: PlanState): string {
  return `${KEY}=${toBase64Url(JSON.stringify(state))}`
}

/** The version in a fragment, or null when there is none or it does not parse. */
export function decodeVersion(hash: string): SharedVersion | null {
  const fragment = hash.replace(/^#/, '')
  if (!fragment.startsWith(`${KEY}=`)) return null
  const json = fromBase64Url(fragment.slice(KEY.length + 1))
  if (json === null) return null
  try {
    const parsed = versionLinkSchema.safeParse(JSON.parse(json))
    return parsed.success
      ? { plan: parsed.data.plan, adjustments: parsed.data.adjustments }
      : null
  } catch {
    return null
  }
}
